import { useEffect, useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Briefcase, Columns3, Plus, Table as TableIcon } from 'lucide-react'

import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { useHasJobsFeature } from '@/hooks/use-has-feature'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { LoadingOverlay } from '@/components/shared/loading'
import { LoadMoreBar } from '@/components/shared/load-more-bar'
import { PipelinePhaseTiles } from '@/components/pipeline/pipeline-phase-tiles'
import { PipelineToolbar } from '@/components/pipeline/pipeline-toolbar'
import { PipelineTable } from '@/components/pipeline/pipeline-table'
import { PipelineBoard } from '@/components/pipeline/pipeline-board'
import { InquirySheet } from '@/components/inquiries/inquiry-sheet'
import {
  buildOwnersById,
  PIPELINE_TILE_FILTER_NAME,
  pipelineBoardShowingLabel,
  pipelineNextSort,
  pipelineShowingLabel,
  pipelineTileJobCount,
  pipelineTileToListFilters,
  type PipelineSort,
  type PipelineTileKey,
} from '@/components/pipeline/pipeline-job-view'
import { useInfinitePipelineJobs, useInvalidateJobAfterQuoteChange, usePipelineSummary } from '@/stores/pipeline.store'
import { organizationStore, useOrganizationUsers } from '@/stores/organization.store'
import { useStore } from '@tanstack/react-store'
import { IncomingReferralsPopover } from '@/components/leads/referrals/incoming-referrals-popover'
import { JobSortDirectionEnum, JobSortFieldEnum } from '@/gen/cortado/v1/settings_pb'

export const Route = createFileRoute('/_app/_home/pipeline/')({
  component: PipelinePageRoute,
})

export function PipelinePageRoute() {
  const navigate = useNavigate()
  const { hasFeature, hasLoaded } = useHasJobsFeature()

  useEffect(() => {
    if (hasLoaded && !hasFeature) {
      navigate({ to: '/dashboard', replace: true })
    }
  }, [hasFeature, hasLoaded, navigate])

  if (!hasLoaded || !hasFeature) {
    return <div className="flex min-h-[50dvh] items-center justify-center">Loading...</div>
  }

  return <PipelinePage />
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedValue(value), delayMs)
    return () => clearTimeout(timeoutId)
  }, [delayMs, value])

  return debouncedValue
}

const DEFAULT_SORT: PipelineSort = {
  field: JobSortFieldEnum.JOB_SORT_FIELD_UNSPECIFIED,
  direction: JobSortDirectionEnum.JOB_SORT_DIRECTION_UNSPECIFIED,
}

export function PipelinePage() {
  useBreadcrumbFromRoute()
  const navigate = useNavigate()
  const invalidateJobs = useInvalidateJobAfterQuoteChange()
  const [isNewLeadOpen, setIsNewLeadOpen] = useState(false)
  const organizationId = useStore(organizationStore, (s) => s.selectedOrganizationId) ?? ''

  const [viewMode, setViewMode] = useState<'table' | 'board'>('board')
  const [activeTile, setActiveTile] = useState<PipelineTileKey>('all')
  const [search, setSearch] = useState('')
  const [ownerId, setOwnerId] = useState('any')
  const [eventDateFrom, setEventDateFrom] = useState<Date | undefined>(undefined)
  const [eventDateTo, setEventDateTo] = useState<Date | undefined>(undefined)
  const [sort, setSort] = useState<PipelineSort>(DEFAULT_SORT)

  const debouncedSearch = useDebouncedValue(search.trim(), 300)

  // Shared toolbar filters (search/owner/event date) + sort — used by the
  // table's list, the board's per-column lists, and (minus sort) the summary.
  const sharedFilterOptions = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      assignedToId: ownerId === 'any' ? undefined : ownerId,
      eventStart: eventDateFrom,
      eventEnd: eventDateTo,
    }),
    [debouncedSearch, ownerId, eventDateFrom, eventDateTo],
  )

  const sortedFilterOptions = useMemo(
    () => ({ ...sharedFilterOptions, sortField: sort.field, sortDirection: sort.direction }),
    [sharedFilterOptions, sort],
  )

  const tileFilters = pipelineTileToListFilters(activeTile)

  const {
    jobs,
    totalCount,
    hasMore,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    hasLoaded,
    isError,
    error,
  } = useInfinitePipelineJobs({
    ...sortedFilterOptions,
    phases: tileFilters.phases,
    paymentStates: tileFilters.paymentStates,
  })

  const {
    buckets,
    stageBuckets,
    balanceDueCents,
    openInvoiceCount,
    overdueInvoiceCount,
    isLoading: isSummaryLoading,
  } = usePipelineSummary(sharedFilterOptions)
  const { users: owners } = useOrganizationUsers()
  const ownersById = useMemo(() => buildOwnersById(owners), [owners])

  const isFiltered =
    activeTile !== 'all' || !!debouncedSearch || ownerId !== 'any' || !!eventDateFrom || !!eventDateTo
  const tileFilterName = PIPELINE_TILE_FILTER_NAME[activeTile]
  // The board's columns each paginate independently, so its toolbar text
  // uses the tile's true total from the summary instead of a loaded/total
  // pair that only makes sense for the table's single paginated list.
  const showingLabel =
    viewMode === 'table'
      ? pipelineShowingLabel(jobs.length, totalCount, tileFilterName, sort)
      : pipelineBoardShowingLabel(pipelineTileJobCount(buckets, activeTile), tileFilterName, sort)

  const handleSortChange = (field: JobSortFieldEnum) => setSort((current) => pipelineNextSort(current, field))

  const handleClearFilters = () => {
    setActiveTile('all')
    setSearch('')
    setOwnerId('any')
    setEventDateFrom(undefined)
    setEventDateTo(undefined)
  }

  if (isLoading || !hasLoaded) {
    return <LoadingOverlay message="Loading pipeline..." />
  }

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Pipeline</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Every customer job from first inquiry to final payment.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as 'table' | 'board')}>
            <TabsList>
              <TabsTrigger value="board">
                <Columns3 />
                Board
              </TabsTrigger>
              <TabsTrigger value="table">
                <TableIcon />
                Table
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {organizationId && <IncomingReferralsPopover organizationId={organizationId} />}
          <Button size="sm" onClick={() => setIsNewLeadOpen(true)}>
            <Plus />
            New lead
          </Button>
        </div>
      </div>

      <PipelinePhaseTiles
        buckets={buckets}
        stageBuckets={stageBuckets}
        balanceDueCents={balanceDueCents}
        openInvoiceCount={openInvoiceCount}
        overdueInvoiceCount={overdueInvoiceCount}
        activeTile={activeTile}
        onSelect={setActiveTile}
        isLoading={isSummaryLoading && buckets.length === 0}
      />

      <PipelineToolbar
        search={search}
        onSearchChange={setSearch}
        eventDateFrom={eventDateFrom}
        eventDateTo={eventDateTo}
        onEventDateChange={({ from, to }) => {
          setEventDateFrom(from)
          setEventDateTo(to)
        }}
        ownerId={ownerId}
        onOwnerChange={setOwnerId}
        owners={owners}
        showingLabel={showingLabel}
      />

      {isError ? (
        <Empty className="min-h-[400px]">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Briefcase />
            </EmptyMedia>
            <EmptyTitle>Couldn&apos;t load the pipeline</EmptyTitle>
            <EmptyDescription>{error?.message || 'Unknown error'}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : jobs.length === 0 ? (
        <Empty className="min-h-[400px]">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Briefcase />
            </EmptyMedia>
            <EmptyTitle>
              {isFiltered ? `Nothing in ${tileFilterName} right now` : 'No jobs yet'}
            </EmptyTitle>
            <EmptyDescription>
              {debouncedSearch
                ? `Nothing matches "${debouncedSearch}" in ${tileFilterName}.`
                : isFiltered
                  ? 'Try clearing the filters.'
                  : 'Jobs for this organization will show up here.'}
            </EmptyDescription>
          </EmptyHeader>
          {isFiltered && (
            <EmptyContent>
              <Button variant="outline" size="sm" onClick={handleClearFilters}>
                Show all records
              </Button>
            </EmptyContent>
          )}
        </Empty>
      ) : viewMode === 'table' ? (
        <>
          <PipelineTable jobs={jobs} ownersById={ownersById} sort={sort} onSortChange={handleSortChange} />
          <LoadMoreBar
            loaded={jobs.length}
            total={totalCount}
            hasMore={hasMore}
            isLoading={isFetchingNextPage}
            onLoadMore={() => fetchNextPage()}
          />
        </>
      ) : (
        <PipelineBoard
          ownersById={ownersById}
          activeTile={activeTile}
          stageBuckets={stageBuckets}
          filterOptions={sortedFilterOptions}
          sort={sort}
          onSortChange={setSort}
        />
      )}

      <InquirySheet
        layout="pipeline"
        mode="create"
        open={isNewLeadOpen}
        onOpenChange={setIsNewLeadOpen}
        onCreated={(inquiry) => {
          // The new lead's job joins the Pipeline lists and counts.
          void invalidateJobs()
          if (inquiry.jobId) navigate({ to: '/pipeline/$id', params: { id: inquiry.jobId } })
        }}
      />
    </div>
  )
}
